import throttle from 'lodash/throttle';
import { useRef, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { IconArrowLeft, IconArrowRight } from '@xing-com/icons';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';
import { GridContainer } from '@xing-com/xing-grid';

import { RecruiterCardsList } from './recruiter-cards-list';
import {
  Title,
  StyledSection,
  Subtitle,
  UpperContainer,
  HideButtons,
  CarouselControls,
  CarouselButtonsWrapper,
  CarouselButton,
  StyledRow,
  StyledColumn,
} from './recruiters-section.styles';

enum CarouselControlsState {
  LEFT_DISABLED,
  RIGHT_DISABLED,
  BOTH_ENABLED,
}

const CARD_SIZE = 346;

export const RecruitersSection: React.VFC = () => {
  const { formatMessage } = useIntl();
  const [carouselControlsState, setCarouselControlsState] = useState(
    CarouselControlsState.LEFT_DISABLED
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const breakpoint = useMediaListener();
  const isHandheld = breakpoint === BREAKPOINTS.handheld;

  const handleResizeHelper = (
    container: HTMLElement,
    carousel: HTMLElement
  ): void => {
    const marginLeft = container.getBoundingClientRect().left;

    carousel.style.marginRight = `-${marginLeft}px`;
    carousel.style.marginLeft = `-${marginLeft}px`;
    carousel.style.paddingLeft = `${marginLeft}px`;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const carouselLastChild = carousel.lastChild as HTMLElement;

    if (carouselLastChild) {
      carouselLastChild.style.marginRight = `0px`;

      const existingSpacing = container?.querySelector<HTMLElement>(
        '[data-spacing="true"]'
      );

      const spacing = existingSpacing
        ? existingSpacing
        : document.createElement('div');
      spacing.style.minWidth = `${marginLeft}px`;
      spacing.style.height = '1px';
      spacing.dataset.spacing = 'true';

      if (!existingSpacing) {
        carousel.append(spacing);
      }
    }
  };

  const handleCarouselScrollHelper = (carousel: HTMLElement): void => {
    const scrollLeft = carousel.scrollLeft;

    if (scrollLeft <= CARD_SIZE / 2) {
      setCarouselControlsState(CarouselControlsState.LEFT_DISABLED);
    } else if (
      scrollLeft >=
      carousel.scrollWidth - carousel.clientWidth - CARD_SIZE
    ) {
      setCarouselControlsState(CarouselControlsState.RIGHT_DISABLED);
    } else {
      setCarouselControlsState(CarouselControlsState.BOTH_ENABLED);
    }
  };

  useEffect(() => {
    const container = containerRef.current;

    const carousel = container?.querySelector<HTMLElement>(
      '[data-xds="Carousel"]'
    );

    if (!container || !carousel) return;

    const handleResize = throttle(
      () => handleResizeHelper(container, carousel),
      100
    );

    window.addEventListener('resize', handleResize, false);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    const carousel = container?.querySelector<HTMLElement>(
      '[data-xds="Carousel"]'
    );

    if (!carousel) return;

    const handleCarouselScroll = throttle(
      () => handleCarouselScrollHelper(carousel),
      100
    );

    carousel.addEventListener('scroll', handleCarouselScroll);

    handleCarouselScroll();

    return () => carousel.removeEventListener('scroll', handleCarouselScroll);
  }, [containerRef]);

  const CarouselControlsComponent = (): JSX.Element => (
    <CarouselControls>
      <CarouselButtonsWrapper>
        <CarouselButton
          aria-label="previous-recruiter-arrow-button"
          data-qa="carousel-previous"
          disabled={
            carouselControlsState === CarouselControlsState.LEFT_DISABLED
          }
          onClick={() => {
            const container = containerRef.current;
            const carousel = container?.querySelector<HTMLElement>(
              '[data-xds="Carousel"]'
            );
            const scrollWidth = isHandheld
              ? CARD_SIZE / 2
              : carousel?.clientWidth || CARD_SIZE;

            carousel?.scrollBy({
              top: 0,
              left: -scrollWidth,
              behavior: 'smooth',
            });
          }}
        >
          <IconArrowLeft height={30} width={45} />
        </CarouselButton>
        <CarouselButton
          aria-label="next-recruiter-arrow-button"
          data-qa="carousel-next"
          disabled={
            carouselControlsState === CarouselControlsState.RIGHT_DISABLED
          }
          onClick={() => {
            const container = containerRef.current;
            const carousel = container?.querySelector<HTMLElement>(
              '[data-xds="Carousel"]'
            );
            const scrollWidth = isHandheld
              ? CARD_SIZE / 2
              : carousel?.clientWidth || CARD_SIZE;

            carousel?.scrollBy({
              top: 0,
              left: scrollWidth,
              behavior: 'smooth',
            });
          }}
        >
          <IconArrowRight height={30} width={45} />
        </CarouselButton>
      </CarouselButtonsWrapper>
    </CarouselControls>
  );

  return (
    <StyledSection>
      <GridContainer type="wide">
        <UpperContainer>
          <div>
            <Title sizeConfined="medium" size="small" forwardedAs="h2">
              {formatMessage({ id: 'RECRUITERS_SECTION_SECOND_TITLE' })}
            </Title>
            <Subtitle size="xxlarge" forwardedAs="p" noMargin>
              {formatMessage({ id: 'RECRUITERS_SECTION_SECOND_SUBTITLE' })}
            </Subtitle>
          </div>
          {!isHandheld ? <CarouselControlsComponent /> : null}
        </UpperContainer>

        <StyledRow>
          <StyledColumn size={12}>
            <HideButtons ref={containerRef}>
              <RecruiterCardsList />
            </HideButtons>
          </StyledColumn>
        </StyledRow>
      </GridContainer>
      {isHandheld ? <CarouselControlsComponent /> : null}
    </StyledSection>
  );
};
